/**
 * ClientRuntime 实现
 *
 * 运行时层，消费 Registry 数据并驱动 React + react-router 渲染。
 *
 * client-runtime 被设计为最后加载的 Mod，当其被实例化时，
 * 所有业务 Mod 已完成注册，可直接执行首次渲染。
 */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as JotaiProvider } from 'jotai';
import { ReactRouterAdapter } from './react-router-adapter.js';
import { RouteServiceProvider } from '@hvsin/mod-route';
import { ClientComponentsContext, ClientGlobalStateContext, ClientServicesContext, ClientHooksContext, } from './context.js';
/**
 * 客户端运行时
 *
 * 消费 Registry 数据，驱动 React 渲染、路由初始化、状态注入。
 * 当此 Mod 被加载时，所有业务 Mod 已完成注册，
 * 构造函数中直接执行首次渲染。
 */
export class ClientRuntime {
    constructor(deps) {
        this.root = null;
        this.deps = {
            pageRouteRegistry: deps.pageRouteRegistry,
            globalStateRegistry: deps.globalStateRegistry,
            componentRegistry: deps.componentRegistry,
            hooksRegistry: deps.hooksRegistry,
            clientServiceRegistry: deps.clientServiceRegistry,
        };
        this.routeService = deps.routeService;
        this.routerAdapter = new ReactRouterAdapter();
        // ─── 初始化路由（所有页面已注册）───
        this.routerAdapter.init(deps.pageRouteRegistry.getPages());
    }
    /**
     * 挂载到 DOM（CSR 模式）
     *
     * 在浏览器端创建 React root 并执行首次渲染。
     */
    mount() {
        const rootElement = document.getElementById('app');
        if (!rootElement) {
            console.error('[ClientRuntime] #app element not found');
            return;
        }
        this.root = createRoot(rootElement);
        this.render();
    }
    /**
     * 重新渲染（用于动态加载新 Mod 时更新路由）
     */
    refresh() {
        this.routerAdapter.updatePages(this.deps.pageRouteRegistry.getPages());
        this.render();
    }
    /**
     * 获取路由适配器
     */
    getRouterAdapter() {
        return this.routerAdapter;
    }
    /**
     * 获取路由服务
     */
    getRouteService() {
        return this.routeService;
    }
    /**
     * 构建 App React 元素（Provider 树 + RouterComponent）
     *
     * SPA 和 SSR 共享此方法构建一致的组件树结构。
     * 传入可选的自定义 RouterComponent 用于 SSR 模式。
     */
    createAppElement(routerComponent) {
        const { globalStateRegistry, componentRegistry, hooksRegistry, clientServiceRegistry, } = this.deps;
        const RouterComp = routerComponent ?? this.routerAdapter.getRouterComponent();
        const routerElement = React.createElement(RouterComp);
        return React.createElement(JotaiProvider, null, React.createElement(ClientGlobalStateContext.Provider, { value: globalStateRegistry.getStates() }, React.createElement(ClientComponentsContext.Provider, { value: componentRegistry.getComponents() }, React.createElement(ClientHooksContext.Provider, { value: hooksRegistry.getHooks() }, React.createElement(ClientServicesContext.Provider, { value: clientServiceRegistry.getServices() }, React.createElement(RouteServiceProvider, {
            routeService: this.routeService,
            children: routerElement,
        }))))));
    }
    render() {
        const appElement = this.createAppElement();
        this.root?.render(appElement);
    }
}
//# sourceMappingURL=client-runtime.js.map